import { getCounterpartyViewRowId } from "@common/helpers/counterpartyViewRowId";
import {
    BillsByCounterpartyItem,
    BillByCounterpartySortValue,
} from "@common/types/bill";
import { BillFilters } from "@common/contracts/bill.contract";
import { BillsByVendorView as BillsByVendorViewType } from "@common/types/views/billViews";
import { View } from "@common/types/views/views";
import { useCallback, useMemo } from "react";
import classNames from "classnames";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { billsByVendorViewConfig } from "./billsByVendorViewConfig";
import styles from "./BillsByVendorView.module.scss";

interface Props {
    view: BillsByVendorViewType;
    billsByVendor: BillsByCounterpartyItem[] | null;
    sort?: BillByCounterpartySortValue;
    onSortChange?: (sort: BillByCounterpartySortValue) => void;
    onDrilldown?: (params: BillFilters) => void;
    emptyView?: () => React.ReactNode;
    footer?: React.ReactNode;
    className?: string;
}

export const BillsByVendorView: React.FC<Props> = ({
    view,
    billsByVendor,
    sort,
    onSortChange,
    onDrilldown,
    emptyView = () => "No vendor data found",
    footer,
    className,
}) => {
    const data = useMemo(() => {
        if (!billsByVendor) {
            return null;
        }
        // Map the data to include the required 'id' field
        return billsByVendor.map((item) => ({
            ...item,
            id: getCounterpartyViewRowId(item.counterpartyId),
        }));
    }, [billsByVendor]);

    const handleDrilldown = useCallback(
        (params: BillFilters) => {
            onDrilldown?.(params);
        },
        [onDrilldown],
    );

    const tableMeta = useMemo(
        () => ({
            callbacks: {
                onDrilldown: handleDrilldown,
            },
        }),
        [handleDrilldown],
    );

    return (
        <ViewTable
            className={classNames(styles.billsByVendorView, className)}
            data={data}
            view={view as View}
            config={billsByVendorViewConfig as any}
            sort={sort}
            onSortChange={onSortChange}
            fixedTopOffsetPx
            emptyView={emptyView}
            tableMeta={tableMeta}
            footer={footer}
        />
    );
};
